import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { StatusBadge } from './StatusBadge';
import { getBranchMeta, getInitials, formatDate } from '../utils/format';
import styles from './AgentCard.module.css';

function MiniTask({ task }) {
  const meta = {
    working: '#38bdf8',
    blocked: '#f87171',
    idle: '#94a3b8',
    done: '#34d399',
  }[task.status] || '#94a3b8';

  return (
    <div className={styles.miniTask}>
      <div className={styles.miniTaskHeader}>
        <span className={styles.miniTaskDot} style={{ backgroundColor: meta }} />
        <span className={styles.miniTaskTitle}>{task.title}</span>
      </div>
      <div className={styles.miniTaskProgress}>
        <div
          className={styles.miniTaskProgressFill}
          style={{ width: `${task.progress ?? 0}%`, backgroundColor: meta }}
        />
      </div>
    </div>
  );
}

export function AgentCard({ agentId, onClose }) {
  const { get } = useApi();
  const [agent, setAgent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchAgent() {
      setLoading(true);
      setError(null);
      try {
        const data = await get(`/api/agents/${agentId}`);
        if (!cancelled) setAgent(data.agent);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAgent();
    return () => {
      cancelled = true;
    };
  }, [agentId, get]);

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.skeletonHeader}>
          <div className={`${styles.skeletonAvatar} skeleton`} />
          <div className={styles.skeletonLines}>
            <div className="skeleton" style={{ height: 20, width: 140 }} />
            <div className="skeleton" style={{ height: 14, width: 100, marginTop: 8 }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className={styles.card}>
        <div className={styles.error}>{error || 'Agent not found'}</div>
      </div>
    );
  }

  const branchMeta = getBranchMeta(agent.branch);
  const queueWarning = (agent.queue_depth || 0) > 10;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close detail panel"
        >
          <X size={18} />
        </button>
      </div>

      <div className={styles.identity}>
        <div
          className={styles.avatar}
          style={{
            backgroundColor: `${branchMeta.color}20`,
            color: branchMeta.color,
          }}
        >
          {getInitials(agent.name)}
        </div>
        <div className={styles.identityText}>
          <h2 className={styles.name}>{agent.name}</h2>
          <p className={styles.role}>{agent.role_title}</p>
          <span
            className={styles.branchBadge}
            style={{
              backgroundColor: `${branchMeta.color}12`,
              color: branchMeta.color,
            }}
          >
            {agent.branch}
          </span>
        </div>
      </div>

      <div className={styles.statusRow}>
        <StatusBadge status={agent.status} size="lg" />
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricValue}>{agent.inbox_count ?? 0}</span>
          <span className={styles.metricLabel}>Inbox</span>
        </div>
        <div className={`${styles.metric} ${queueWarning ? styles.metricWarning : ''}`}>
          <span className={styles.metricValue}>
            {agent.queue_depth ?? 0}
            {queueWarning && <AlertTriangle size={14} aria-label="Queue depth warning" />}
          </span>
          <span className={styles.metricLabel}>Queue</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricValue}>{agent.assigned_tasks?.length ?? 0}</span>
          <span className={styles.metricLabel}>Tasks</span>
        </div>
      </div>

      {agent.current_task_title && (
        <div className={styles.currentWork}>
          <h3 className={styles.sectionTitle}>Current Work</h3>
          <div className={styles.currentTaskCard}>
            <span className={styles.currentTaskLabel}>{agent.current_task_title}</span>
            {agent.started_at && (
              <span className={styles.currentTaskMeta}>Started {formatDate(agent.started_at)}</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.taskSection}>
        <h3 className={styles.sectionTitle}>Active Tasks</h3>
        {agent.assigned_tasks?.length > 0 ? (
          <div className={styles.miniTaskList}>
            {agent.assigned_tasks.slice(0, 10).map((task) => (
              <MiniTask key={task.id} task={task} />
            ))}
          </div>
        ) : (
          <p className={styles.empty}>No active tasks</p>
        )}
      </div>
    </div>
  );
}
